import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Menu } from 'react-feather';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { getTeamsMenuConfigAPI, publishTeamsMenuAPI } from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import ConfirmModal from '../../../Components/shared/ConfirmModal';
import { companyUrl } from '../../../utils/mhNav';
import styles from './TeamsMenuBuilder.module.scss';

const ROUTES = ['book', 'list', 'freebusy', 'mom', 'help', 'custom'];

export default function TeamsMenuBuilder() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pubOpen, setPubOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getTeamsMenuConfigAPI();
      setCfg(res.data);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const move = (idx, dir) => {
    const buttons = [...(cfg.buttons || [])];
    const j = idx + dir;
    if (j < 0 || j >= buttons.length) return;
    [buttons[idx], buttons[j]] = [buttons[j], buttons[idx]];
    setCfg({ ...cfg, buttons });
  };

  const updateBtn = (idx, field, val) => {
    const buttons = [...(cfg.buttons || [])];
    buttons[idx] = { ...buttons[idx], [field]: val };
    setCfg({ ...cfg, buttons });
  };

  const addBtn = () => {
    if ((cfg.buttons || []).length >= 6) {
      toast.warning('Recommended max is 6 buttons');
      return;
    }
    setCfg({
      ...cfg,
      buttons: [...(cfg.buttons || []), { id: `b-${Date.now()}`, label: 'New', actionRoute: 'book', payloadName: 'new' }],
    });
  };

  if (loading && !cfg) {
    return (
      <div>
        <PageHeader title="Teams menu" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Channels', path: companyUrl('channels') }, { label: 'Teams' }]} />
        <LoadingSkeleton rows={5} cols={2} />
      </div>
    );
  }

  if (error && !cfg) {
    return (
      <div>
        <PageHeader title="Teams menu" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Channels', path: companyUrl('channels') }]} />
        <EmptyState icon={AlertTriangle} title="Error" description={error.message} actionLabel="Retry" onAction={load} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Teams menu builder"
        breadcrumbs={[
          { label: 'Company', path: companyUrl('dashboard') },
          { label: 'Channels', path: companyUrl('channels') },
          { label: 'Teams' },
        ]}
        secondaryActions={[
          { label: 'Add button', onClick: addBtn, variant: 'outline-secondary' },
          { label: 'Preview', onClick: () => toast.info('Preview is the right panel'), variant: 'outline-secondary' },
          { label: 'Publish', onClick: () => setPubOpen(true), variant: 'primary' },
        ]}
      />

      <Row className="g-3">
        <Col lg={6}>
          <Card>
            <Card.Body>
              <h2 className="h6">Welcome card</h2>
              <Form.Group className="mb-2">
                <Form.Label>Bot name</Form.Label>
                <Form.Control value={cfg.botName} onChange={(e) => setCfg({ ...cfg, botName: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Welcome title</Form.Label>
                <Form.Control value={cfg.welcomeTitle} onChange={(e) => setCfg({ ...cfg, welcomeTitle: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Welcome body</Form.Label>
                <Form.Control as="textarea" rows={2} value={cfg.welcomeBody} onChange={(e) => setCfg({ ...cfg, welcomeBody: e.target.value })} />
              </Form.Group>
              <h2 className="h6 mt-3">Buttons</h2>
              {(cfg.buttons || []).map((b, i) => (
                <div key={b.id} className="border rounded p-2 mb-2">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span className={styles.dragHint}>
                      <Menu size={16} />
                    </span>
                    <Button size="sm" variant="light" onClick={() => move(i, -1)}>
                      ↑
                    </Button>
                    <Button size="sm" variant="light" onClick={() => move(i, 1)}>
                      ↓
                    </Button>
                  </div>
                  <Form.Control className="mb-1" size="sm" value={b.label} onChange={(e) => updateBtn(i, 'label', e.target.value)} />
                  <Form.Select className="mb-1" size="sm" value={b.actionRoute} onChange={(e) => updateBtn(i, 'actionRoute', e.target.value)}>
                    {ROUTES.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control size="sm" placeholder="Payload name" value={b.payloadName} onChange={(e) => updateBtn(i, 'payloadName', e.target.value)} />
                </div>
              ))}
              <Button variant="link" size="sm" className="p-0" onClick={addBtn}>
                + Add button
              </Button>
              {(cfg.buttons || []).length > 6 && <p className="text-warning small mb-0">Recommended max is 6 buttons</p>}
              <Form.Group className="mt-3">
                <Form.Label>Fallback message</Form.Label>
                <Form.Control as="textarea" rows={2} value={cfg.fallbackMessage} onChange={(e) => setCfg({ ...cfg, fallbackMessage: e.target.value })} />
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6}>
          <div className={styles.previewPane}>
            <p className="small text-muted mb-2">Teams preview</p>
            <div className={styles.previewCard}>
              <div className="fw-semibold">{cfg.welcomeTitle}</div>
              <div className="small mt-1">{cfg.welcomeBody}</div>
              <div className={styles.previewBtns}>
                {(cfg.buttons || []).map((b) => (
                  <Button key={b.id} size="sm" variant="outline-primary" className="text-truncate">
                    {b.label}
                  </Button>
                ))}
              </div>
            </div>
            <p className="small text-muted mt-3 mb-0">{cfg.fallbackMessage}</p>
          </div>
        </Col>
      </Row>

      <ConfirmModal
        isOpen={pubOpen}
        onClose={() => setPubOpen(false)}
        onConfirm={async () => {
          try {
            await publishTeamsMenuAPI(cfg);
            toast.success('Published');
            setPubOpen(false);
          } catch (e) {
            toast.error(e.message);
          }
        }}
        title="Publish Teams menu?"
        message="This updates the live bot card (mock)."
        confirmLabel="Publish"
      />
    </div>
  );
}
